/**
 * 模型族：下拉框里应该出现的**真实**选项。
 *
 * 为什么要有这一层：
 * 后端路由表里有 35 个 slug，但其中
 *   · 20 个是网关 / duomi 都没有的上游（Seedream、Sunburst、Topaz、Seedance、万相）
 *   · 6 个是同一个模型的别名（gpt-image-2 被拆成「官方 / 图生图 / 渠道 / 渠道编辑」）
 * 把它们原样渲染进下拉，用户看到的就是一屏假选项 + 一屏重复项。
 *
 * 所以前端只认「模型」，调用方式（文生图 / 图生图）由
 * **表单里有没有参考图**自动决定 —— 见 slugForFamily()。
 *
 * 清单以 `GET /api/aigc/models` 的 `families` 为唯一真源；
 * 下面这份是接口不可用时的兜底（离线 mock、后端没起、刚启动还没拉到），
 * 内容与后端 registry.FAMILIES 保持一致，改一处要同步另一处。
 *
 * ⚠️ 同步的是**过滤后**的 `registry.families()`，不是全量 `FAMILIES`：
 * 视频档只留已实测的（后端 `config.ONLY_VERIFIED_VIDEO`，2026-09-30 起）。
 * 兜底清单里不许出现没跑通过的视频模型 —— 兜底的意义是「后端连不上也能干活」，
 * 不是「偷偷把删掉的选项放回来」。
 */

import type { ModelFamilyInfo } from '@/api/types'

/**
 * 图片档的能力 —— **与后端 `image_sizes.py` 逐项对齐**，也是兜底清单必须带
 * `capabilities` 的原因（视频档下面那段注释说的同一件事）。
 *
 * 2026-09-30 第二轮补：用户报「比例不够多」之后，详情页与批量页的
 * 「输出尺寸 / 比例」候选表的第二三段**就是从 `capabilities.aspect_ratios` 补的**。
 * 后端连不上时若这里是空的，候选会静默缩回平台那两三档 ——
 * 用户看到的是「比例又变少了」，而不是「后端没连上」。
 *
 * ⚠️ 这两份与后端是**同步**关系（改后端要改这里），不是第二真源：
 * 在线时的唯一真源仍然是 `GET /api/aigc/models`。
 */
const DUOMI_RATIOS = [
  '1:1', '4:5', '3:4', '2:3', '9:16', '1:2',
  '5:4', '4:3', '3:2', '16:9', '1.91:1', '2:1', '21:9',
]
/** songshuai 网关只认这 6 个（后端 `GATEWAY_IMAGE_RATIOS`）—— 有 4:5，没有 1:2 / 21:9 */
const GATEWAY_RATIOS = ['1:1', '4:5', '3:4', '2:3', '9:16', '16:9']

export const FALLBACK_FAMILIES: ModelFamilyInfo[] = [
  {
    id: 'gpt-image-2.5',
    name: 'GPT Image 2.5',
    kind: 'image',
    available: true,
    channel: 'duomi',
    channelLabel: 'duomi 直连',
    unitPrice: 0.1,
    unit: '张',
    textSlug: 'gpt-image-2-5-flare-text-to-image-economy',
    imageSlug: 'gpt-image-2-5-flare-image-to-image-economy',
    img2imgSameAsText: false,
    note: '实测约 25s 出图，日常首选',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: { aspect_ratios: DUOMI_RATIOS },
  },
  {
    id: 'gpt-image-2',
    name: 'GPT Image 2',
    kind: 'image',
    available: true,
    channel: 'duomi',
    channelLabel: 'duomi 直连',
    unitPrice: 0.1,
    unit: '张',
    textSlug: 'gpt-image-2',
    imageSlug: 'gpt-image-2-image-to-image-official-stable',
    img2imgSameAsText: false,
    note: '细节更稳，但慢约 5 倍（约 130s）',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: { aspect_ratios: DUOMI_RATIOS },
  },
  {
    id: 'nano-banana-2',
    name: 'Nano Banana 2',
    kind: 'image',
    available: true,
    channel: 'gateway',
    channelLabel: 'songshuai 网关',
    unitPrice: 0.2,
    unit: '张',
    textSlug: 'nano-banana2-gemini31flash-text-to-image-official-stable',
    imageSlug: 'nano-banana2-gemini31flash-image-to-image-official-stable',
    img2imgSameAsText: false,
    note: null,
    verified: false,
    reason: null,
    suggest: null,
    capabilities: { aspect_ratios: GATEWAY_RATIOS },
  },
  {
    id: 'nano-banana-pro',
    name: 'Nano Banana Pro',
    kind: 'image',
    available: true,
    channel: 'gateway',
    channelLabel: 'songshuai 网关',
    unitPrice: 0.3,
    unit: '张',
    textSlug: 'nano-banana-pro',
    imageSlug: 'nano-banana-pro',
    img2imgSameAsText: true,
    note: '同一模型兼顾文生图与图生图',
    verified: false,
    reason: null,
    suggest: null,
    capabilities: { aspect_ratios: GATEWAY_RATIOS },
  },
  {
    id: 'nano-banana-2-lite',
    name: 'Nano Banana 2 Lite',
    kind: 'image',
    available: true,
    channel: 'gateway',
    channelLabel: 'songshuai 网关',
    unitPrice: 0.1,
    unit: '张',
    textSlug: 'nano-banana-2-lite',
    imageSlug: 'nano-banana-2-lite',
    img2imgSameAsText: true,
    note: '最便宜，适合批量打样',
    verified: false,
    reason: null,
    suggest: null,
    capabilities: { aspect_ratios: GATEWAY_RATIOS },
  },
  /*
    视频档：**只留已实测跑通的**。

    2026-09-30 用户原话「不需要那么多选项，留下测过能用的」—— 原来这里还有两条
    网关档（`minimax-h3` ¥0.30/秒、`google-omni` ¥0.15/秒），本项目从没成功跑过
    （网关 `minimax-h3` 图生视频 0 成 2 败、`google-omni` 零记录），一起撤了，
    视频下拉从 16 条收到 4 条。判据与完整清单见后端 `registry._YOUKOU_VERIFIED`
    与 `config.ONLY_VERIFIED_VIDEO`（后者设 0 可恢复全量）。

    2026-10-01 回到 6 条 —— **不是把撤掉的放回来，是两条各自跑通之后转正**：
    `youkou-wan-3-0`（贰春，落地 youkou-40472.mp4）与 `google-omni`
    （改挂速创 API，6 秒 720p 出片）。同一条判据，同一道门槛。

    下面的 capabilities 是**真值**（= 后端 `_YOUKOU_VIDEO_MODELS` 的断言），
    不是演示值：后端连不上时，视频页的清晰度 / 比例控件、参考图与参考视频上传框
    全靠它，回落全局清单会让「只有 768P 的模型」显示出 480/720/1080 三个假档。
  */
  {
    id: 'youkou-minimax-h3',
    name: 'MiniMax H3 特价',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 0.07,
    unit: '秒',
    textSlug: 'youkou-minimax-h3',
    imageSlug: 'youkou-minimax-h3',
    img2imgSameAsText: true,
    note: '最便宜 ¥0.07/秒 · 768P（无 480/720/1080 档）· 1-15s · 输出 H.264（实测约 3 分钟出片）',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['768P'],
      aspect_ratios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16', 'adaptive'],
      ref_image_max: 9,
      ref_video_max: 3,
    },
  },
  {
    id: 'youkou-sd20-channel',
    name: 'SD 2.0 渠道特价',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 0.98,
    unit: '秒',
    textSlug: 'youkou-sd20-channel',
    imageSlug: 'youkou-sd20-channel',
    img2imgSameAsText: true,
    note: '¥0.18/0.42/0.98 秒（480P/720P/1080P）· 4-15s · 支持 1080P',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['480P', '720P', '1080P'],
      aspect_ratios: ['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16'],
      ref_image_max: 9,
      ref_video_max: 2,
    },
  },
  {
    id: 'youkou-sd20-auto-face',
    name: 'SD 2.0 自动过人脸',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 0.8,
    unit: '次',
    textSlug: 'youkou-sd20-auto-face',
    imageSlug: 'youkou-sd20-auto-face',
    img2imgSameAsText: true,
    note: '¥0.8/次 · 720p · 4-15s · 自动过人脸，真人模特素材被拒时优先换它',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['720p'],
      aspect_ratios: ['21:9', '16:9', '3:2', '4:3', '1:1', '3:4', '2:3', '9:16'],
      ref_image_max: 9,
      ref_video_max: 0,
    },
  },
  {
    id: 'youkou-sd20-tehui',
    name: 'SD 2.0 特惠版',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 1.0,
    unit: '次',
    textSlug: 'youkou-sd20-tehui',
    imageSlug: 'youkou-sd20-tehui',
    img2imgSameAsText: true,
    note: '¥1/次 · 720p · 4-15s · 按次计价，拖长不加价',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['720p'],
      aspect_ratios: ['21:9', '16:9', '3:2', '4:3', '1:1', '3:4', '2:3', '9:16'],
      ref_image_max: 9,
      ref_video_max: 3,
    },
  },
  /*
    2026-10-01 补两条（用户在视频工作台点名要）：
      · `youkou-wan-3-0` —— 贰春侧实测出片（480P×5s，落地 youkou-40472.mp4，
        入账 ¥0.79）；
      · `google-omni` —— 从网关切到**速创 API**。原先走网关那条从接入起
        「从没跑过」（后端 config.py 原话），2026-10-01 实测网关 402 余额 ¥0.00、
        aihubcc 403（¥0.09）、bltcy 403（¥0.79），三家都欠费，只有速创能跑
        （6 秒 720p，99 秒出片）。所以它的 channel 是 `wuyin` 而不是 `gateway`。
  */
  {
    id: 'youkou-wan-3-0',
    name: '阿里云 Wan 3.0',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 0.8,
    unit: '秒',
    textSlug: 'youkou-wan-3-0',
    imageSlug: 'youkou-wan-3-0',
    img2imgSameAsText: true,
    note: '¥0.158/0.4/0.8 秒（480P/720P/1080P）· 2-30s · 参考视频最多 5 条，本清单最高',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['480P', '720P', '1080P'],
      aspect_ratios: ['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16'],
      ref_image_max: 10,
      ref_video_max: 5,
    },
  },
  {
    id: 'google-omni',
    name: 'Google Omni',
    kind: 'video',
    available: true,
    channel: 'wuyin',
    channelLabel: '速创 API',
    unitPrice: 0.15,
    unit: '秒',
    textSlug: 'google-omni-text-to-video',
    imageSlug: 'google-omni-image-to-video',
    // ⚠️ false：这一档文生/图生是**两个 upstream slug**，不能像贰春那样合并
    img2imgSameAsText: false,
    note: '速创 API 直连 · 6/8/10 秒 · 参考图只取第 1 张',
    verified: true,
    reason: null,
    suggest: null,
    // 速创按「分辨率」而不是「清晰度档」出片，所以**不给 resolutions** ——
    // 给空数组的含义是「这个模型什么都不能选」（见 types.ts 那段注释），
    // 与「没说」是两回事。不给字段 = 回落全局默认，那才是实际的语义。
    capabilities: {
      aspect_ratios: ['16:9', '9:16'],
      ref_image_max: 1,
      ref_video_max: 0,
    },
  },
  // ---- 文本（LLM）----
  // 文案页的模型下拉也吃同一份清单。后端 registry.FAMILIES 里这两个是唯一
  // 真实可用的文案模型，别在前端另写一份。
  {
    id: 'deepseek-flash',
    name: 'DeepSeek V4.1 Flash',
    kind: 'text',
    available: true,
    channel: 'llm',
    channelLabel: 'DeepSeek 官方',
    unitPrice: 0.1,
    unit: '条',
    textSlug: 'deepseek-flash',
    imageSlug: null,
    img2imgSameAsText: false,
    note: '推理模型 · 文案性价比首选',
    verified: true,
    reason: null,
    suggest: null,
  },
  {
    id: 'deepseek-v4-pro',
    name: 'DeepSeek V4 Pro',
    kind: 'text',
    available: true,
    channel: 'llm',
    channelLabel: 'DeepSeek 官方',
    unitPrice: 0.2,
    unit: '条',
    textSlug: 'deepseek-v4-pro',
    imageSlug: null,
    img2imgSameAsText: false,
    note: '更强推理 · 长文案与复杂卖点',
    verified: true,
    reason: null,
    suggest: null,
  },
  // ---- 音频（语音合成，2026-10-08）----
  //
  // **它不出现在任何模型下拉里** —— `/tts` 页面选的是「音色」，不是模型
  // （音色清单来自 `GET /api/aigc/voices`，与数字人页同一份）。
  // 这条存在的唯一理由是：`JobCenterContext.submit()` 会拿表单里的 family id
  // 去 `familyById()` 折成真实路由 slug。清单里没有它时 `familyById` 返回 null，
  // 那个 id 被**原样当 slug 发出去** —— 后端 `resolve()` 按「未知 slug」降级到
  // 同 kind 的默认（恰好也是它），于是这次侥幸跑通。侥幸不该被依赖。
  //
  // `unit`/`unitPrice` 与后端 `pricing.PROVIDER_PRICE["minimax-tts"]` 一致（¥0.05/条）。
  //
  // ⚠️ `channel` 必须跟着**当前默认引擎**走（2026-10-08：`minimax-direct`，
  // 合成为官方直连）。它对应后端 `registry._tts(..., "minimax-tts", ...)`
  // → `_channel_of("minimax-tts")` → `minimax`。
  // 这条曾经写的是 `apilio`（旧默认引擎那一档）。抄错的代价不是编译错，
  // 而是**界面把「余额不足该去哪充值」指到另一家公司**。
  {
    id: 'tts',
    name: '语音合成',
    kind: 'audio',
    available: true,
    channel: 'minimax',
    channelLabel: 'MiniMax 官方',
    unitPrice: 0.05,
    unit: '条',
    textSlug: 'tts',
    imageSlug: null,
    img2imgSameAsText: true,
    note: '口播稿 → mp3 · 音色在页面里选',
    verified: true,
    reason: null,
    suggest: null,
  },
]

/** 默认模型：图片首选 GPT Image 2.5（duomi 直连，实测最快且不依赖网关余额）。 */
export const DEFAULT_FAMILY_ID: Record<'image' | 'video' | 'audio' | 'text', string> = {
  image: 'gpt-image-2.5',
  /*
    视频首选 MiniMax H3 特价（贰春，¥0.07/秒）—— 全网关 / 全贰春里最便宜的一档，
    而且是**图生视频实测出过片**的（4 次成功）。

    这里改过值：原先是网关的 `minimax-h3`（¥0.30/秒）。它连同 `google-omni`
    一起在 2026-09-30 从下拉里撤了（从没成功跑过），默认值必须跟着走 ——
    否则新任务的初始模型是个下拉里根本选不出来的项，
    `ModelSelect` 会在挂载后立刻把你选的模型改掉。
  */
  video: 'youkou-minimax-h3',
  text: 'deepseek-flash',
  /*
    音频只有一档（`tts`，默认由 MiniMax 官方直连合成，¥0.05/条）。**这个值不驱动任何下拉** ——
    `/tts` 页面上选的是音色，模型（`speech-2.8-hd`）由后端的配音引擎开关决定；
    它存在是为了让 `ModelsContext.normalize()` 有地方落，
    以及「哪天 audio 多一条」时默认值有明确出处。
  */
  audio: 'tts',
}

export const familiesOf = (list: ModelFamilyInfo[], kind: 'image' | 'video' | 'audio' | 'text') =>
  list.filter((f) => f.kind === kind && f.available)

export const familyById = (list: ModelFamilyInfo[], id: string) =>
  list.find((f) => f.id === id) ?? null

/**
 * 表单里存的旧值可能是 slug（升级前遗留的草稿），转成 family id。
 * 找不到就返回 null，由调用方兜底到默认模型。
 */
export const familyIdFromLegacy = (list: ModelFamilyInfo[], value: string) => {
  const direct = list.find((f) => f.id === value)
  if (direct) return direct.id
  const bySlug = list.find((f) => f.textSlug === value || f.imageSlug === value)
  return bySlug?.id ?? null
}

/**
 * 模型 + 「有没有参考图」→ 实际提交的 slug。
 *
 * 这是整套聚合的落点：用户选的是模型，不是调用方式。
 */
export const slugForFamily = (family: ModelFamilyInfo, hasRef: boolean): string => {
  if (hasRef && family.imageSlug) return family.imageSlug
  return family.textSlug ?? family.imageSlug ?? ''
}

/** 下拉里那一行显示什么：名字 + 关键差异，不堆参数。 */
export const familyUpsell = (f: ModelFamilyInfo): string => {
  const bits: string[] = []
  if (f.unitPrice) bits.push(`¥${f.unitPrice.toFixed(2)}/${f.unit}`)
  if (f.verified) bits.push('已实测')
  return bits.join(' · ')
}
